"use client";

import {
  ArrowRight,
  Play,
  Clock,
  DollarSign,
  TrendingUp,
  BanknoteArrowUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const ValueProps = ({ className }: { className?: string }) => (
  <div className={cn("grid grid-cols-2 gap-3 lg:gap-4", className)}>
    <div className="bg-secondary/50 border border-border rounded-xl p-3 lg:p-4 text-center">
      <Clock className="w-5 h-5 lg:w-6 lg:h-6 text-primary mx-auto mb-1.5 lg:mb-2" />
      <div className="text-base lg:text-lg font-bold">6 Weeks</div>
      <div className="text-xs lg:text-sm text-muted-foreground">
        To Full Automation
      </div>
    </div>
    <div className="bg-secondary/50 border border-border rounded-xl p-3 lg:p-4 text-center">
      <TrendingUp className="w-5 h-5 lg:w-6 lg:h-6 text-primary mx-auto mb-1.5 lg:mb-2" />
      <div className="text-base lg:text-lg font-bold">80% Time</div>
      <div className="text-xs lg:text-sm text-muted-foreground">
        Saved on Tasks
      </div>
    </div>
    <div className="bg-secondary/50 border border-border rounded-xl p-3 lg:p-4 text-center">
      <DollarSign className="w-5 h-5 lg:w-6 lg:h-6 text-primary mx-auto mb-1.5 lg:mb-2" />
      <div className="text-base lg:text-lg font-bold">6.5x ROI</div>
      <div className="text-xs lg:text-sm text-muted-foreground">
        Average Return
      </div>
    </div>
    <div className="bg-secondary/50 border border-border rounded-xl p-3 lg:p-4 text-center">
      <BanknoteArrowUp className="w-5 h-5 lg:w-6 lg:h-6 text-primary mx-auto mb-1.5 lg:mb-2" />
      <div className="text-base lg:text-lg font-bold">$18M+</div>
      <div className="text-xs lg:text-sm text-muted-foreground">
        Raised Fundings
      </div>
    </div>
  </div>
);

const Hero = () => {
  return (
    <section className="min-h-screen flex flex-col justify-center pt-24 pb-16 px-6">
      <div className="container max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Main Content */}
          <div className="flex-1 animate-fade-in">
            <span className="text-sm md:text-base text-accent font-medium mb-4 block">
              Abdul Rehman
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight md:leading-tight lg:leading-tight max-w-4xl mb-6">
              <span className="text-gradient">
                AI Workflow & Automation Engineer
              </span>{" "}
              eliminating manual busy work for your team.
            </h1>

            {/* Mobile Value Props */}
            <ValueProps className="mb-8 lg:hidden" />

            <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mb-6 leading-relaxed">
              I help businesses{" "}
              <span className="text-primary font-semibold">
                save $50K-$200K annually
              </span>{" "}
              by replacing manual workflows with intelligent AI agents. Your
              team gets their time back, your business scales faster.
            </p>

            <div className="bg-secondary/50 border border-border rounded-xl p-4 mb-8 max-w-2xl">
              <p className="text-lg text-muted-foreground">
                <span className="font-semibold text-primary">
                  &ldquo;Rehman transformed our 8-hour daily process into 15
                  minutes. We&apos;re now processing 10x more orders with the
                  same team.&rdquo;
                </span>
                <br />
                <span className="text-sm">
                  — Sarah Chen, Operations Director at TechFlow
                </span>
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <Button
                size="lg"
                className="group text-base px-6 rounded-full bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90"
                onClick={() =>
                  document
                    .getElementById("contact")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                Get Your Free Automation Audit
                <ArrowRight
                  size={18}
                  className={cn(
                    "ml-2 transition-transform duration-300",
                    "group-hover:translate-x-1"
                  )}
                />
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="text-base px-6 rounded-full group"
                onClick={() =>
                  document
                    .getElementById("contact")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                <Play
                  size={16}
                  className="mr-2 group-hover:scale-110 transition-transform"
                />
                See Results & Get Started
              </Button>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span>Available for new projects</span>
              </div>
              <div>
                <span className="font-semibold">250K+</span> hours automated
              </div>
              <div>
                <a
                  href="/about"
                  className="text-primary hover:text-accent transition-colors underline underline-offset-2"
                >
                  See my track record →
                </a>
              </div>
            </div>
          </div>

          {/* Desktop Value Props */}
          <div className="hidden lg:block lg:w-[240px] lg:self-center">
            <ValueProps className="lg:grid-cols-1" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
